/**
 * NotificationProvider - Zentrale Notification-Verwaltung
 *
 * Bietet einen Context für einheitliche Snackbar-Notifications
 * im gesamten Restaurant Dashboard.
 */

import React, {
  createContext,
  useCallback,
  useContext,
  useState,
  ReactNode,
} from "react";
import { Alert, AlertColor, Snackbar, Slide, SlideProps } from "@mui/material";

interface Notification {
  id: string;
  message: string;
  severity: AlertColor;
  duration?: number;
}

interface NotificationContextValue {
  showSuccess: (message: string) => void;
  showError: (message: string) => void;
  showWarning: (message: string) => void;
  showInfo: (message: string) => void;
}

const NotificationContext = createContext<NotificationContextValue | null>(
  null
);

function SlideTransition(props: SlideProps) {
  return <Slide {...props} direction="up" />;
}

interface NotificationProviderProps {
  children: ReactNode;
}

export function NotificationProvider({ children }: NotificationProviderProps) {
  const [notification, setNotification] = useState<Notification | null>(null);

  const showNotification = useCallback(
    (message: string, severity: AlertColor, duration: number = 4000) => {
      setNotification({
        id: Date.now().toString(),
        message,
        severity,
        duration,
      });
    },
    []
  );

  const showSuccess = useCallback(
    (message: string) => showNotification(message, "success", 4000),
    [showNotification]
  );

  const showError = useCallback(
    (message: string) => showNotification(message, "error", 6000),
    [showNotification]
  );

  const showWarning = useCallback(
    (message: string) => showNotification(message, "warning", 5000),
    [showNotification]
  );

  const showInfo = useCallback(
    (message: string) => showNotification(message, "info", 4000),
    [showNotification]
  );

  const handleClose = useCallback(
    (_event?: React.SyntheticEvent | Event, reason?: string) => {
      if (reason === "clickaway") {
        return;
      }
      setNotification(null);
    },
    []
  );

  const value: NotificationContextValue = {
    showSuccess,
    showError,
    showWarning,
    showInfo,
  };

  return (
    <NotificationContext.Provider value={value}>
      {children}
      <Snackbar
        open={Boolean(notification)}
        autoHideDuration={notification?.duration ?? 4000}
        onClose={handleClose}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        TransitionComponent={SlideTransition}
      >
        {notification ? (
          <Alert
            onClose={handleClose}
            severity={notification.severity}
            variant="filled"
            elevation={6}
            sx={{
              width: "100%",
              minWidth: 300,
              fontSize: "0.95rem",
              "& .MuiAlert-message": {
                padding: "4px 0",
              },
            }}
          >
            {notification.message}
          </Alert>
        ) : undefined}
      </Snackbar>
    </NotificationContext.Provider>
  );
}

/**
 * Hook zum Anzeigen von Notifications
 */
export function useNotification(): NotificationContextValue {
  const context = useContext(NotificationContext);
  if (!context) {
    throw new Error(
      "useNotification must be used within a NotificationProvider"
    );
  }
  return context;
}
